/** @format */

import {useEffect, useState} from 'react';

import PropTypes from 'prop-types';
import classNames from 'classnames';
import {Icon} from '@sttark/design-system';

export function Select({
    initialValue,
    placeholder,
    onChange,
    label,
    value,
    name,
    disabled,
    error,
    options,
    className,
    size,
    ...props
} = props) {
    // delete newestds prop
    delete props.newestds;

    // state
    const [showList, setShowList] = useState(false);
    const [currentLabel, setCurrentLabel] = useState(initialValue);

    options = options.map(option => {
        if (typeof option === 'string' || option instanceof String) {
            return {label: option, value: option};
        } else {
            return option;
        }
    });

    const hasError = !(error == null || error === '');
    const hasLabel = label && label !== '';

    const fieldClass = classNames(
        'field select-field--shadowed',
        showList && '--open border-bottom-0',
        hasError && 'field--error'
    );
    const inputClass = classNames(
        'control',
        'select--shadowed',
        showList && '--open',
        showList && 'border-bottom-0',
        'd-flex',
        'align-items-center',
        'justify-content-between',
        {'w-100': !size},
        className
    );

    // make sure default value rerenders every time initial value changes
    useEffect(() => {
        setCurrentLabel(initialValue);
    }, [initialValue]);

    const toggleDropdown = () => {
        setShowList(false);
        setTimeout(() => window.removeEventListener('click', toggleDropdown), 200);
    };

    return (
        <div className={`${fieldClass} ${className}`}>
            <div
                name={name}
                id={name}
                className={inputClass}
                disabled={disabled}
                aria-invalid={hasError}
                onClick={() => {
                    setShowList(!showList);

                    if (!showList) {
                        setTimeout(() => {
                            window.addEventListener('click', toggleDropdown);
                        }, 200);
                    }
                }}
                {...props}>
                {currentLabel && <p className="select-value--shadowed p-0 m-0">{currentLabel}</p>}
                {!currentLabel && placeholder && (
                    <p className="select-value--shadowed select-value--shadowed-placeholder p-0 m-0">
                        {placeholder}
                    </p>
                )}
                <span>
                    <Icon name="navigatedown" color="black" />
                </span>
                {showList && (
                    <div className="w-100 position-absolute select-option--container bg-structure-white p-2 border-all">
                        {options.map((option, i) => (
                            <Option
                                key={i}
                                option={option}
                                isLastOption={i === options.length - 1}
                                handleOptionClick={value => {
                                    onChange(value.value, value.label);
                                    setShowList(false);

                                    // do not change the input value if option is a trigger
                                    if (value.value !== 'cta') setCurrentLabel(value.label);
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>
            {hasLabel && currentLabel && (
                <label className="floating-label" htmlFor={name}>
                    <span className="color-action-primary">{label}</span>
                </label>
            )}
            {hasError && <p className="color-alert-negative">{error}</p>}
        </div>
    );
}

const Option = ({option, handleOptionClick, isLastOption = false}) => {
    return (
        <div
            className={`select-field--shadowed w-100 z-index-dropdown ${
                !isLastOption && 'border-bottom'
            }`}>
            <div
                className="py-2 overflow-auto"
                style={{maxHeight: 200}}
                onClick={() => handleOptionClick(option)}>
                <div
                    className={`select-field-value--shadowed p-2 d-flex align-items-center justify-content-between ${option.className}`}>
                    {option.label}
                    <div className="right-side d-flex align-items-center justify-content-end">
                        {option?.rightLabel && (
                            <h5
                                onClick={
                                    option.rightLabel.handleClick
                                        ? () => option.rightLabel.handleClick(option.value)
                                        : null
                                }
                                className={`right-side-label font-body-bold-md text-right m-3 ${
                                    option.rightLabel.className ? option.rightLabel.className : ''
                                }`}>
                                {option.rightLabel.label}
                            </h5>
                        )}

                        {option?.icons &&
                            option.icons.map((item, index) => (
                                <span
                                    className="me-3"
                                    onClick={() => item.handleClick(option.value)}
                                    key={index}>
                                    <Icon name={item.name} color={item.color} key={index} />
                                </span>
                            ))}
                    </div>
                </div>
                {option.description && (
                    <div
                        className="description ps-2 pe-2"
                        style={{
                            fontSize: '16px',
                            color: '#67737C',
                            whiteSpace: 'normal'
                        }}>
                        {option.description}
                    </div>
                )}
            </div>
        </div>
    );
};
Select.propTypes = {
    placeholder: PropTypes.string,
    label: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.any,
    onChange: PropTypes.func,
    initialValue: PropTypes.string,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            value: PropTypes.any.isRequired,
            className: PropTypes.string,
            description: PropTypes.string,
            icons: PropTypes.arrayOf(
                PropTypes.shape({
                    name: PropTypes.string.isRequired,
                    color: PropTypes.string,
                    handleClick: PropTypes.func
                })
            ),
            rightLabel: PropTypes.shape({
                label: PropTypes.string,
                className: PropTypes.string,
                handleClick: PropTypes.func
            })
        })
    ).isRequired,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    rows: PropTypes.number,
    className: PropTypes.string,
    size: PropTypes.number
};

Select.defaultProps = {
    label: 'Label Name',
    options: [],
    disabled: false,
    placeholder: 'Select Option'
};
